import axios, { AxiosResponse } from 'axios';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  QRResponse,
  AuthConfig,
  BlogPost,
  CreateBlogPostRequest,
  UpdateBlogPostRequest,
} from '../types/api';
import { API_ORIGIN } from '@/config/api';

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_ORIGIN,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: 토큰 자동 추가
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 응답 인터셉터: 에러 처리
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 인증 에러 시 토큰 제거
      localStorage.removeItem('authToken');
      // 로그인 페이지가 아닌 경우에만 리다이렉트
      if (!window.location.pathname.includes('/admin')) {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

// API 함수들
export const api = {
  // 인증 관련 API
  auth: {
    // QR 코드 생성
    getQRCode: async (username = 'hmchoi'): Promise<QRResponse> => {
      const response: AxiosResponse<ApiResponse<QRResponse>> = await apiClient.get(
        `/api/blog/auth/qr?username=${username}`
      );
      return response.data.data!;
    },

    // 로그인
    login: async (credentials: LoginRequest): Promise<LoginResponse> => {
      const response: AxiosResponse<ApiResponse<LoginResponse>> = await apiClient.post(
        '/api/blog/auth/login',
        credentials
      );
      return response.data.data!;
    },

    // 토큰 검증
    verifyToken: async (): Promise<{ username: string; valid: boolean; message: string }> => {
      const response: AxiosResponse<ApiResponse<{ username: string; valid: boolean; message: string }>> =
        await apiClient.get('/api/blog/auth/verify');
      return response.data.data!;
    },

    // 로그아웃
    logout: async (): Promise<string> => {
      const response: AxiosResponse<ApiResponse<string>> = await apiClient.post('/api/blog/auth/logout');
      return response.data.data!;
    },

    // 설정 확인
    getConfig: async (): Promise<AuthConfig> => {
      const response: AxiosResponse<ApiResponse<AuthConfig>> = await apiClient.get('/api/blog/auth/config');
      return response.data.data!;
    },
  },

  // 블로그 포스트 관련 API
  posts: {
    // 모든 포스트 조회
    getAll: async (params?: {
      category?: string;
      tag?: string;
      limit?: number;
      offset?: number;
    }): Promise<BlogPost[]> => {
      const response: AxiosResponse<ApiResponse<BlogPost[]>> = await apiClient.get('/api/blog/posts', {
        params,
      });
      return response.data.data!;
    },

    // 특정 포스트 조회
    getById: async (id: number): Promise<BlogPost> => {
      const response: AxiosResponse<ApiResponse<BlogPost>> = await apiClient.get(`/api/blog/posts/id/${id}`);
      return response.data.data!;
    },

    // 포스트 생성 (관리자 전용)
    create: async (post: CreateBlogPostRequest): Promise<BlogPost> => {
      const response: AxiosResponse<ApiResponse<BlogPost>> = await apiClient.post('/api/blog/admin/posts', post);
      return response.data.data!;
    },

    // 포스트 수정 (관리자 전용)
    update: async (id: number, post: UpdateBlogPostRequest): Promise<BlogPost> => {
      const response: AxiosResponse<ApiResponse<BlogPost>> = await apiClient.put(`/api/blog/admin/posts/${id}`, post);
      return response.data.data!;
    },

    // 포스트 삭제 (관리자 전용)
    delete: async (id: number): Promise<string> => {
      const response: AxiosResponse<ApiResponse<string>> = await apiClient.delete(`/api/blog/admin/posts/${id}`);
      return response.data.data!;
    },
  },

  // 카테고리 및 태그 API
  categories: {
    getAll: async (): Promise<Array<{ id: number; name: string; slug: string }>> => {
      const response: AxiosResponse<ApiResponse<Array<{ id: number; name: string; slug: string }>>> = await apiClient.get('/api/blog/categories');
      return response.data.data!;
    },
  },

  tags: {
    getAll: async (): Promise<Array<{ id: number; name: string; slug: string }>> => {
      const response: AxiosResponse<ApiResponse<Array<{ id: number; name: string; slug: string }>>> = await apiClient.get('/api/blog/tags');
      return response.data.data!;
    },
  },

  // 헬스 체크
  health: {
    check: async (): Promise<string> => {
      const response: AxiosResponse<string> = await apiClient.get('/');
      return response.data;
    },
  },
};

export default api;
